import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { fetchMatchingRules, runAssistRule } from './services/api';
import ActionChat from './ActionChat';

const WidgetApp = ({ config }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('rules'); // 'rules' or 'actions'
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rulesError, setRulesError] = useState(null);
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [executionState, setExecutionState] = useState({
    loading: false,
    error: null,
    response: null,
  });

  const pageUrl = useMemo(() => window.location.href, []);
  const [fieldLevelRules, setFieldLevelRules] = useState([]);
  const injectedIconsRef = useRef(new Map()); // Track injected icons by element

  useEffect(() => {
    const loadRules = async () => {
      setRulesLoading(true);
      setRulesError(null);
      try {
        const data = await fetchMatchingRules(config.apiBaseUrl, pageUrl);
        if (data.status === 'SUCCESS') {
          const allRules = Array.isArray(data.rules) ? data.rules : [];
          setRules(allRules);
          // Separate field-level rules
          const fieldRules = allRules.filter(rule => rule.ruleScope === 'FIELD' && rule.fieldSelector);
          setFieldLevelRules(fieldRules);
        } else {
          setRulesError(data.message || 'Failed to load assist rules');
        }
      } catch (err) {
        console.error('Error loading rules:', err);
        let errorMessage = err.message || 'Failed to load assist rules';
        
        // Check if it's the JSON parsing error
        if (err.message && err.message.includes('Unexpected token')) {
          errorMessage = 'Received HTML instead of JSON. This might be the ngrok warning page. Please visit the ngrok URL directly in your browser first and click "Visit Site" to bypass the warning.';
        }
        
        setRulesError(errorMessage);
      } finally {
        setRulesLoading(false);
      }
    };

    loadRules();
  }, [config.apiBaseUrl, pageUrl]);

  useEffect(() => {
    if (isOpen && !rulesLoading && rules.length === 0) {
      setMode('actions');
    }
  }, [isOpen, rulesLoading, rules.length]);

  const readFileContents = async (fileInputSelector) => {
    if (!fileInputSelector) {
      return null;
    }

    const fileInput = document.querySelector(fileInputSelector);
    if (!fileInput) {
      console.warn(`File input not found for selector: ${fileInputSelector}`);
      return null;
    }

    if (fileInput.type !== 'file') {
      console.warn(`Element found for selector "${fileInputSelector}" is not a file input`);
      return null;
    }

    if (!fileInput.files || fileInput.files.length === 0) {
      console.warn(`No file selected in file input: ${fileInputSelector}`);
      return null;
    }

    const file = fileInput.files[0];
    
    // Check if it's a text file (basic check by extension)
    const fileName = file.name.toLowerCase();
    const textFileExtensions = ['.txt', '.csv', '.json', '.xml', '.html', '.css', '.js', '.md', '.log'];
    const isTextFile = textFileExtensions.some(ext => fileName.endsWith(ext)) || 
                       file.type.startsWith('text/') || 
                       file.type === 'application/json' ||
                       file.type === 'application/xml';

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        let contents = e.target.result;
        
        // If read as DataURL (base64), extract just the base64 part (remove data:type;base64, prefix)
        if (!isTextFile && typeof contents === 'string' && contents.startsWith('data:')) {
          const base64Index = contents.indexOf(',');
          if (base64Index !== -1) {
            contents = contents.substring(base64Index + 1);
          }
        }
        
        resolve({
          contents: contents,
          fileName: file.name,
          fileType: file.type || fileName.substring(fileName.lastIndexOf('.')),
          size: file.size,
        });
      };
      
      reader.onerror = (e) => {
        reject(new Error(`Failed to read file: ${file.name}`));
      };
      
      // For text files, read as text. For binary files (PDF, Word, etc.), read as base64
      if (isTextFile) {
        // Read as text for text files
        reader.readAsText(file);
      } else {
        // Read as base64 for binary files (PDF, Word, Excel, etc.)
        // This allows Tika to extract text from them on the backend
        reader.readAsDataURL(file);
      }
    });
  };

  // Handle field rule click
  const handleFieldRuleClick = useCallback(async (rule, fieldElement) => {
    setSelectedRuleId(rule.id);
    setIsOpen(true);
    setExecutionState({ loading: true, error: null, response: null });

    // For field-level rules, try to get context from the contextSelector
    // If contextSelector points to a file input or the field itself, use surrounding context instead
    let contextHtml = null;
    
    if (rule.contextSelector) {
      const contextElement = document.querySelector(rule.contextSelector);
      
      // Check if contextSelector points to a file input or the field itself
      if (contextElement && (contextElement.type === 'file' || contextElement === fieldElement)) {
        // Use the field's parent or surrounding context instead
        const parentElement = fieldElement.parentElement;
        if (parentElement) {
          contextHtml = parentElement.innerHTML || '';
        }
      } else if (contextElement) {
        // Normal context selector - try to capture it
        contextHtml = captureContextHtml(rule.contextSelector);
      }
    }
    
    // If still no context, try to use the field element's parent or surrounding context
    if (!contextHtml || contextHtml.trim() === '') {
      const parentElement = fieldElement.parentElement;
      if (parentElement) {
        contextHtml = parentElement.innerHTML || '';
      }
    }
    
    // Add field-specific information
    const fieldLabel = fieldElement.labels?.[0]?.textContent || 
                     fieldElement.getAttribute('aria-label') || 
                     fieldElement.placeholder || 
                     fieldElement.name || 
                     fieldElement.id || 
                     'Field';
    const fieldValue = fieldElement.value || '';
    const fieldInfo = `\n\n=== FIELD INFORMATION ===\n${fieldLabel}: ${fieldValue || '(empty)'}`;
    contextHtml = (contextHtml || '') + fieldInfo;

    // Check if prompt contains [fileData] placeholder
    const promptHasFileDataPlaceholder = rule.prompt && rule.prompt.includes('[fileData]');
    
    // Read file contents if file input selector is specified OR if prompt has [fileData] placeholder
    let fileData = null;
    if (rule.fileInputSelector || promptHasFileDataPlaceholder) {
      if (!rule.fileInputSelector) {
        setExecutionState({
          loading: false,
          error: 'Prompt contains [fileData] placeholder but no File Input Selector is configured for this rule.',
          response: null,
        });
        return;
      }
      
      try {
        fileData = await readFileContents(rule.fileInputSelector);
        
        // If prompt has [fileData] placeholder, file is required
        if (promptHasFileDataPlaceholder && !fileData) {
          setExecutionState({
            loading: false,
            error: 'Prompt contains [fileData] placeholder but no file is selected. Please select a file first.',
            response: null,
          });
          return;
        }
        
        if (fileData) {
          // Increased limit to 10MB since we use Tika to extract text (extracted text is much smaller)
          const maxFileSize = 10 * 1024 * 1024; // 10MB
          if (fileData.size > maxFileSize) {
            setExecutionState({
              loading: false,
              error: `File "${fileData.fileName}" is too large (${(fileData.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 10MB.`,
              response: null,
            });
            return;
          }
        }
      } catch (err) {
        setExecutionState({
          loading: false,
          error: `Failed to read file: ${err.message}`,
          response: null,
        });
        return;
      }
    }

    const payload = {
      pageUrl,
      contextHtml,
    };

    if (fileData) {
      payload.fileContents = fileData.contents;
      payload.fileName = fileData.fileName;
      payload.fileType = fileData.fileType;
    }

    try {
      const result = await runAssistRule(config.apiBaseUrl, rule.id, payload);
      if (result.status === 'SUCCESS') {
        setExecutionState({
          loading: false,
          error: null,
          response: result,
        });
      } else {
        setExecutionState({
          loading: false,
          error: result.message || 'Assistant failed to run the rule.',
          response: null,
        });
      }
    } catch (err) {
      console.error('Error executing rule:', err);
      let errorMessage = err.message || 'Assistant failed to run the rule.';
      if (err.message && err.message.includes('Unexpected token')) {
        errorMessage = 'Received HTML instead of JSON. This might be the ngrok warning page. Please visit the ngrok URL directly in your browser first and click "Visit Site" to bypass the warning.';
      }
      setExecutionState({
        loading: false,
        error: errorMessage,
        response: null,
      });
    }
  }, [config.apiBaseUrl, pageUrl]);

  // Function to inject assist icons next to fields
  const injectFieldIcons = useCallback(() => {
    // Clean up old icons that are no longer valid
    const currentElements = new Set();
    
    fieldLevelRules.forEach(rule => {
      try {
        const elements = document.querySelectorAll(rule.fieldSelector);
        elements.forEach(element => {
          currentElements.add(element);
          
          // Skip if icon already exists for this element
          if (injectedIconsRef.current.has(element)) {
            return;
          }

          // Check if element is visible
          const rect = element.getBoundingClientRect();
          if (rect.width === 0 && rect.height === 0) {
            return; // Element is not visible
          }

          // Create icon container
          const iconContainer = document.createElement('div');
          iconContainer.className = 'assist-field-icon-container';
          iconContainer.setAttribute('data-assist-rule-id', rule.id);
          iconContainer.style.cssText = `
            position: absolute !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 28px !important;
            height: 28px !important;
            border-radius: 4px !important;
            border: 1px solid #e5e7eb !important;
            background: #ffffff !important;
            color: #2563eb !important;
            cursor: pointer !important;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
            z-index: 2147483647 !important;
            transition: background-color 0.2s ease, border-color 0.2s ease !important;
            margin-left: 8px !important;
            vertical-align: middle !important;
          `;

          // Add hover styles
          iconContainer.addEventListener('mouseenter', () => {
            iconContainer.style.background = '#f3f4f6';
            iconContainer.style.borderColor = '#d1d5db';
          });
          iconContainer.addEventListener('mouseleave', () => {
            iconContainer.style.background = '#ffffff';
            iconContainer.style.borderColor = '#e5e7eb';
          });

          // Add click handler
          iconContainer.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            handleFieldRuleClick(rule, element);
          });

          // Add icon SVG
          iconContainer.innerHTML = `
            <svg class="assist-field-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="width: 16px; height: 16px; display: block;">
              <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor" opacity="0.9"/>
              <path d="M19 3L19.5 5.5L22 6L19.5 6.5L19 9L18.5 6.5L16 6L18.5 5.5L19 3Z" fill="currentColor" opacity="0.7"/>
              <path d="M5 15L5.5 17.5L8 18L5.5 18.5L5 21L4.5 18.5L2 18L4.5 17.5L5 15Z" fill="currentColor" opacity="0.7"/>
              <path d="M20 16L20.3 17.3L21.6 17.6L20.3 17.9L20 19.2L19.7 17.9L18.4 17.6L19.7 17.3L20 16Z" fill="currentColor" opacity="0.6"/>
            </svg>
          `;

          // Position the icon to the left of the field
          const fieldParent = element.parentElement;
          
          // Calculate position relative to the field element
          const elementRect = element.getBoundingClientRect();
          
          // Try to insert before the element in the DOM
          if (fieldParent) {
            // Make parent relative if not already (needed for absolute positioning)
            const parentPosition = window.getComputedStyle(fieldParent).position;
            if (parentPosition === 'static') {
              fieldParent.style.position = 'relative';
            }
            
            // Insert the icon before the element in the DOM
            fieldParent.insertBefore(iconContainer, element);
            
            // Position absolutely relative to parent
            iconContainer.style.position = 'absolute';
            
            // Calculate position: element's position relative to parent - icon width - small gap
            const elementLeft = element.offsetLeft;
            const elementTop = element.offsetTop;
            const elementHeight = element.offsetHeight || elementRect.height;
            const iconWidth = 28; // Icon width in pixels
            
            // Position to the left of the field with a small gap
            iconContainer.style.left = `${elementLeft - iconWidth - 8}px`;
            iconContainer.style.top = `${elementTop + (elementHeight / 2) - 14}px`;
            iconContainer.style.transform = 'translateY(0)';
          } else {
            // Fallback: append to body with fixed positioning
            document.body.appendChild(iconContainer);
            iconContainer.style.position = 'fixed';
            iconContainer.style.left = `${elementRect.left - 36}px`; // 28px icon + 8px gap
            iconContainer.style.top = `${elementRect.top + (elementRect.height / 2) - 14}px`;
          }

          // Track the injected icon
          injectedIconsRef.current.set(element, iconContainer);
        });
      } catch (err) {
        console.error(`Error injecting icon for rule ${rule.id} with selector ${rule.fieldSelector}:`, err);
      }
    });

    // Remove icons for elements that no longer match any rule
    injectedIconsRef.current.forEach((iconContainer, element) => {
      if (!currentElements.has(element) || !document.contains(element)) {
        if (iconContainer.parentNode) {
          iconContainer.parentNode.removeChild(iconContainer);
        }
        injectedIconsRef.current.delete(element);
      }
    });
  }, [fieldLevelRules, handleFieldRuleClick]);

  // Watch for DOM changes and inject field icons
  useEffect(() => {
    if (fieldLevelRules.length === 0) {
      // Clean up icons if no field-level rules
      injectedIconsRef.current.forEach((iconContainer) => {
        if (iconContainer.parentNode) {
          iconContainer.parentNode.removeChild(iconContainer);
        }
      });
      injectedIconsRef.current.clear();
      return;
    }

    // Function to perform injection with retry logic
    const performInjection = () => {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        // Small delay to ensure DOM is fully rendered
        setTimeout(() => {
          injectFieldIcons();
        }, 50);
      });
    };

    // Initial injection - wait for DOM to be ready
    if (document.readyState === 'loading') {
      // DOM is still loading, wait for it
      const domReadyHandler = () => {
        performInjection();
        document.removeEventListener('DOMContentLoaded', domReadyHandler);
      };
      document.addEventListener('DOMContentLoaded', domReadyHandler);
    } else {
      // DOM is already ready, inject immediately but with a small delay
      // to ensure any dynamic content has been rendered
      performInjection();
    }
    
    // Also trigger injection when fieldLevelRules change (in case rules load after DOM is ready)
    // This ensures injection happens when rules are loaded asynchronously
    // Use a longer delay to give time for dynamic content to render
    setTimeout(() => {
      performInjection();
    }, 300);

    // Watch for DOM changes
    const observer = new MutationObserver(() => {
      // Debounce to avoid excessive calls
      setTimeout(() => {
        injectFieldIcons();
      }, 100);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      document.removeEventListener('DOMContentLoaded', performInjection);
      observer.disconnect();
      // Clean up all injected icons
      injectedIconsRef.current.forEach((iconContainer) => {
        if (iconContainer.parentNode) {
          iconContainer.parentNode.removeChild(iconContainer);
        }
      });
      injectedIconsRef.current.clear();
    };
  }, [fieldLevelRules, injectFieldIcons]);

  const captureContextHtml = (selector) => {
    if (!selector) {
      return null;
    }
    const element = document.querySelector(selector);
    if (!element) {
      return null;
    }
    
    // Get the innerHTML
    let context = element.innerHTML;
    
    // Find all form inputs within the element and capture their values with labels
    const formInputs = element.querySelectorAll('input, textarea, select');
    if (formInputs.length > 0) {
      const formData = [];
      formInputs.forEach((input) => {
        const inputInfo = {
          type: input.type || input.tagName.toLowerCase(),
          name: input.name || '',
          id: input.id || '',
          value: '',
          label: ''
        };
        
        // Get the value based on input type
        if (input.type === 'checkbox' || input.type === 'radio') {
          inputInfo.value = input.checked ? input.value || 'checked' : 'unchecked';
        } else if (input.tagName.toLowerCase() === 'select') {
          inputInfo.value = input.value;
          // Get selected option text
          const selectedOption = input.options[input.selectedIndex];
          if (selectedOption) {
            inputInfo.value += ` (${selectedOption.text})`;
          }
        } else {
          inputInfo.value = input.value || '';
        }
        
        // Get the label for this input
        let label = '';
        if (input.id) {
          const labelElement = element.querySelector(`label[for="${input.id}"]`);
          if (labelElement) {
            label = labelElement.textContent.trim();
          }
        }
        // If no label found by 'for' attribute, check for parent label
        if (!label && input.closest('label')) {
          label = input.closest('label').textContent.trim();
        }
        // If still no label, check for preceding label element
        if (!label) {
          let prev = input.previousElementSibling;
          while (prev && prev.tagName.toLowerCase() !== 'label') {
            prev = prev.previousElementSibling;
          }
          if (prev && prev.tagName.toLowerCase() === 'label') {
            label = prev.textContent.trim();
          }
        }
        // Check for aria-label
        if (!label && input.getAttribute('aria-label')) {
          label = input.getAttribute('aria-label');
        }
        // Check for placeholder as fallback
        if (!label && input.placeholder) {
          label = `[${input.placeholder}]`;
        }
        
        inputInfo.label = label;
        
        // Only include if there's a value or it's a checkbox/radio
        if (inputInfo.value || input.type === 'checkbox' || input.type === 'radio') {
          formData.push(inputInfo);
        }
      });
      
      // Append form data to context
      if (formData.length > 0) {
        context += '\n\n=== FORM INPUT VALUES ===\n';
        formData.forEach((input) => {
          const identifier = input.label || input.name || input.id || 'Unlabeled input';
          context += `${identifier} (${input.type}): ${input.value}\n`;
        });
      }
    }
    
    return context;
  };

  const handleRuleClick = async (rule) => {
    setSelectedRuleId(rule.id);
    setExecutionState({ loading: true, error: null, response: null });

    const contextHtml = captureContextHtml(rule.contextSelector);
    if (!contextHtml) {
      setExecutionState({
        loading: false,
        error: `Unable to find context for selector "${rule.contextSelector}".`,
        response: null,
      });
      return;
    }

    // Check if prompt contains [fileData] placeholder
    const promptHasFileDataPlaceholder = rule.prompt && rule.prompt.includes('[fileData]');
    
    // Read file contents if file input selector is specified OR if prompt has [fileData] placeholder
    let fileData = null;
    if (rule.fileInputSelector || promptHasFileDataPlaceholder) {
      if (!rule.fileInputSelector) {
        setExecutionState({
          loading: false,
          error: 'Prompt contains [fileData] placeholder but no File Input Selector is configured for this rule.',
          response: null,
        });
        return;
      }
      
      try {
        fileData = await readFileContents(rule.fileInputSelector);
        
        // If prompt has [fileData] placeholder, file is required
        if (promptHasFileDataPlaceholder && !fileData) {
          setExecutionState({
            loading: false,
            error: 'Prompt contains [fileData] placeholder but no file is selected. Please select a file first.',
            response: null,
          });
          return;
        }
        
        // If no file is selected and placeholder is not present, that's okay - just continue without it
        if (fileData) {
          // Increased limit to 10MB since we use Tika to extract text (extracted text is much smaller)
          const maxFileSize = 10 * 1024 * 1024; // 10MB
          if (fileData.size > maxFileSize) {
            setExecutionState({
              loading: false,
              error: `File "${fileData.fileName}" is too large (${(fileData.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 10MB.`,
              response: null,
            });
            return;
          }
        }
      } catch (err) {
        setExecutionState({
          loading: false,
          error: `Failed to read file: ${err.message}`,
          response: null,
        });
        return;
      }
    }

    const payload = {
      pageUrl,
      contextHtml,
    };

    // Add file data if available
    if (fileData) {
      payload.fileContents = fileData.contents;
      payload.fileName = fileData.fileName;
      payload.fileType = fileData.fileType;
    }

    try {
      const result = await runAssistRule(config.apiBaseUrl, rule.id, payload);

      if (result.status === 'SUCCESS') {
        setExecutionState({
          loading: false,
          error: null,
          response: result,
        });
      } else {
        setExecutionState({
          loading: false,
          error: result.message || 'Assistant failed to run the rule.',
          response: null,
        });
      }
    } catch (err) {
      console.error('Error executing rule:', err);
      let errorMessage = err.message || 'Assistant failed to run the rule.';
      
      // Check if it's the JSON parsing error
      if (err.message && err.message.includes('Unexpected token')) {
        errorMessage = 'Received HTML instead of JSON. This might be the ngrok warning page. Please visit the ngrok URL directly in your browser first and click "Visit Site" to bypass the warning.';
      }
      
      setExecutionState({
        loading: false,
        error: errorMessage,
        response: null,
      });
    }
  };

  const togglePanel = () => {
    setIsOpen((prev) => {
      const newState = !prev;
      console.log('Panel toggled, new state:', newState);
      return newState;
    });
  };

  const renderResponse = () => {
    if (executionState.loading) {
      return (
        <div className="assist-loading">
          <div className="assist-spinner"></div>
          <p className="assist-status">Running assistant…</p>
        </div>
      );
    }

    if (executionState.error) {
      return <p className="assist-error">{executionState.error}</p>;
    }

    if (!executionState.response) {
      return <p className="assist-placeholder">Select a rule to run the assistant.</p>;
    }

    const { answer, sources } = executionState.response;

    return (
      <div className="assist-response">
        <h4>Assistant Response</h4>
        <div className="assist-answer">
          <ReactMarkdown>{answer || 'No answer returned.'}</ReactMarkdown>
        </div>
        {Array.isArray(sources) && sources.length > 0 && (
          <div className="assist-sources">
            <h5>Sources</h5>
            <ul>
              {sources.map((source, index) => (
                <li key={`${source.fileName || 'source'}-${index}`}>
                  <strong>{source.fileName || 'Source'}:</strong>{' '}
                  <span>{source.text ? source.text.substring(0, 120) : 'No preview'}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <button
        className={`assist-toggle-button ${config.iconPosition}`}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('Assist button clicked, isOpen:', isOpen);
          togglePanel();
        }}
        aria-label="Open Assist Panel"
      >
        <svg className="assist-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor" opacity="0.9"/>
          <path d="M19 3L19.5 5.5L22 6L19.5 6.5L19 9L18.5 6.5L16 6L18.5 5.5L19 3Z" fill="currentColor" opacity="0.7"/>
          <path d="M5 15L5.5 17.5L8 18L5.5 18.5L5 21L4.5 18.5L2 18L4.5 17.5L5 15Z" fill="currentColor" opacity="0.7"/>
          <path d="M20 16L20.3 17.3L21.6 17.6L20.3 17.9L20 19.2L19.7 17.9L18.4 17.6L19.7 17.3L20 16Z" fill="currentColor" opacity="0.6"/>
        </svg>
      </button>

      <div 
        className={`assist-panel ${isOpen ? 'open' : ''}`}
        style={{
          display: 'flex'
        }}
      >
        <div className="assist-panel-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {mode === 'rules' && executionState.response && (
              <button
                className="assist-back"
                onClick={() => {
                  setSelectedRuleId(null);
                  setExecutionState({ loading: false, error: null, response: null });
                }}
                aria-label="Back to rules"
              >
                ←
              </button>
            )}
            <h3>Intelligent Assistant</h3>
          </div>
          <button className="assist-close" onClick={togglePanel} aria-label="Close Assist Panel">
            ✕
          </button>
        </div>

        <div className="assist-mode-tabs">
          <button
            className={`assist-mode-tab ${mode === 'rules' ? 'active' : ''}`}
            onClick={() => setMode('rules')}
          >
            Standard
          </button>
          <button
            className={`assist-mode-tab ${mode === 'actions' ? 'active' : ''}`}
            onClick={() => setMode('actions')}
          >
            Custom
          </button>
        </div>

        <div className="assist-panel-body">
          {mode === 'actions' ? (
            <ActionChat config={config} />
          ) : executionState.response ? (
            <div className="assist-response-container">{renderResponse()}</div>
          ) : (
            <>
              {rulesLoading ? (
                <p className="assist-status">Loading rules…</p>
              ) : rulesError ? (
                <p className="assist-error">{rulesError}</p>
              ) : rules.length === 0 ? (
                <p className="assist-placeholder">No assist rules match this page.</p>
              ) : (
                <div className="assist-rule-list">
                  {rules.map((rule) => (
                    <button
                      key={rule.id}
                      className={`assist-rule-button ${selectedRuleId === rule.id ? 'active' : ''}`}
                      onClick={() => handleRuleClick(rule)}
                    >
                      {rule.displayName || `Rule ${rule.id}`}
                    </button>
                  ))}
                </div>
              )}
              {executionState.loading && (
                <div className="assist-response-container">{renderResponse()}</div>
              )}
              {executionState.error && !executionState.response && (
                <div className="assist-response-container">{renderResponse()}</div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default WidgetApp;

